import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Check, ChevronRight, Plus, Minus, Landmark, Scale,
  BarChart3, ShieldCheck, Handshake, Search, ShieldPlus, Megaphone,
  MessageCircle, FileSpreadsheet, MapPin, Phone
} from "lucide-react";
import { useSeo } from "@/lib/seo";
import { BRAND } from "@/data/site";

const TEL = BRAND.phone.replace(/\s/g, ""); // +918537942196
const WA = BRAND.whatsapp.replace(/[^\d]/g, "");
const WA_URL = `https://wa.me/${WA}`;

const DATA_FIELDS = [
  "GSTIN of Buyer", "Buyer Company Name",
  "Invoice Number & Date", "Invoice Value",
  "Place of Supply (State)", "Taxable Value",
  "Tax Rate (%)", "IGST / CGST / SGST Amount",
  "IRN (Invoice Reference Number)", "Invoice Type (Regular / SEZ / Deemed Export)",
  "E-Invoice Status", "Filing Period (Month-wise)",
];

const REVEALS = [
  "Who is buying from whom — buyer-seller network",
  "Exact invoice values and transaction volumes",
  "State-wise sales distribution",
  "Industry category by what they sell or buy",
  "Turnover verification for loan eligibility",
  "Supplier-buyer relationship mapping",
  "Seasonal sales patterns and cyclicality",
];

const USE_CASES = [
  { icon: Landmark, title: "Banks & NBFCs", body: "Verify exact annual turnover from GST invoices for loan approval. Cross-check claimed revenue against actual GSTR-1 filings before sanctioning credit — no more relying on self-declared income." },
  { icon: Scale, title: "CA Firms", body: "Analyze a client's buyer network and GST compliance status. Identify filing gaps, reconcile invoice-level turnover, and benchmark clients against industry peers using real transaction data." },
  { icon: BarChart3, title: "Market Research", body: "Industry-wise actual sales volume — not self-reported data. Analyze real invoice-level transactions segmented by state, industry, and turnover band to produce accurate market sizing reports." },
  { icon: ShieldCheck, title: "Credit Agencies", body: "Real transaction data for credit scoring. Invoice-level GSTR-1 data gives a granular, auditable view of a company's revenue consistency, payment volumes, and buyer diversity." },
  { icon: Handshake, title: "Wholesale Suppliers", body: "Find buyers who actively purchase in your product category. Identify companies sourcing the same goods you supply — build targeted prospect lists from live invoice data." },
  { icon: Search, title: "Competitor Analysis", body: "See who your competitors are selling to. Map buyer networks from GSTR-1 data to identify cross-sell opportunities, competitor gaps, and underserved markets." },
  { icon: ShieldPlus, title: "Insurance", body: "Commercial turnover-based premium calculation. Use verified GST invoice turnover data for accurate risk assessment, liability structuring, and premium calculation — eliminates guesswork." },
  { icon: Megaphone, title: "B2B Sales Teams", body: "Find companies actively buying in your industry. Build targeted prospect lists from real invoice data — not scraped directories — filtered by state, product category and transaction volume." },
];

const COMPARISON = [
  { f: "Data Freshness", d: "Monthly updated", o: "6-12 months old" },
  { f: "Coverage", d: "All 28 States + 8 UTs", o: "Limited states" },
  { f: "Verification", d: "Multi-source verified", o: "Single source" },
  { f: "Delivery", d: "24 hours", o: "3-7 days" },
  { f: "Format", d: "Excel / CSV / API", o: "Excel only" },
  { f: "Support", d: "WhatsApp — 7 days a week", o: "Email only" },
  { f: "Custom Filters", d: "State, industry, turnover range", o: "Basic only" },
  { f: "Compliance", d: "100% legal", o: "Questionable" },
];

const SAMPLE_ROWS = [
  { i: 1, buyer: "Apex Traders Pvt Ltd", gstin: "27AABCA1234A1Z5", date: "15-Apr-2026", value: "₹2,45,000", state: "Maharashtra", rate: "18%", taxable: "₹2,07,627", blur: false },
  { i: 2, buyer: "NovaTech Solutions", gstin: "24AACCS5678B2Z6", date: "18-Apr-2026", value: "₹85,000", state: "Gujarat", rate: "12%", taxable: "₹75,893", blur: false },
  { i: 3, buyer: "Kavery Industries Pvt Ltd", gstin: "29AABCK9012C3Z7", date: "21-Apr-2026", value: "₹4,12,500", state: "Karnataka", rate: "18%", taxable: "₹3,49,576", blur: true },
  { i: 4, buyer: "Meridian Wholesale Co.", gstin: "07AAECM3456D4Z8", date: "24-Apr-2026", value: "₹1,68,000", state: "Delhi", rate: "5%", taxable: "₹1,60,000", blur: true },
  { i: 5, buyer: "Sunridge Distributors", gstin: "33AAGCS7890E5Z9", date: "27-Apr-2026", value: "₹6,80,000", state: "Tamil Nadu", rate: "18%", taxable: "₹5,76,271", blur: true },
];

const PRICING = [
  { t: "Custom Data Pull", d: "Based on your filters — state, industry, turnover range. We scope the exact dataset you need.", cta: "Get Custom Quote", href: "/contact", featured: false },
  { t: "Pan India Pack", d: "Complete GST sales purchase database — all states, all industries. Comprehensive B2B leads database India.", cta: "Get Pricing", href: "/contact", featured: true, badge: "Most Popular" },
  { t: "Monthly Subscription", d: "Fresh data every month — always updated. Ideal for sales teams and marketing agencies.", cta: "Contact for Plans", href: "/contact", featured: false },
];

const STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana",
  "Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur",
  "Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
];
const UTS = [
  "Andaman & Nicobar Islands","Chandigarh","Dadra & Nagar Haveli and Daman & Diu","Delhi",
  "Jammu & Kashmir","Ladakh","Lakshadweep","Puducherry",
];

const FAQS = [
  { q: "What is GST sales purchase data?", a: "GST sales purchase data is a structured database containing business transaction details filed under GST returns (GSTR-1 and GSTR-3B). It includes company name, GSTIN, sales turnover, purchase amounts, input tax credit, industry category, state, and filing status. Businesses use this data for lead generation, market research, credit assessment, and targeted B2B marketing." },
  { q: "How can I download a GST registered companies list?", a: "You can get a comprehensive GST registered companies list download from Datorra. Share your requirements — target state, industry, turnover range — and we'll deliver a verified list in Excel/CSV format within 24 hours. Contact us via WhatsApp at +91 85379 42196 or through our contact page." },
  { q: "What is the price of GST database in India?", a: "GST database pricing depends on your specific requirements — the number of records, states covered, industries targeted, and data fields needed. Datorra provides custom pricing based on your exact filters. Contact us for a personalized quote." },
  { q: "How to buy GST data for marketing?", a: "To buy GST data for marketing: (1) Identify your target audience — state, industry, company size. (2) Contact a trusted gst data provider in india like Datorra. (3) Share your requirements on WhatsApp. (4) Review the custom quote. (5) Receive your verified dataset in Excel/CSV within 24 hours. Import into your CRM and start your campaigns." },
  { q: "Is buying GST sales purchase data legal in India?", a: "Yes. GST registration data is public information available on the government GST portal. GST sales and purchase summary data used for business intelligence, market research, and B2B marketing is legally permissible. Datorra ensures 100% compliance with data sourcing regulations." },
  { q: "What is a B2B leads database with GST number?", a: "A b2b leads database india with GST number is a company database that includes each business's GST Identification Number (GSTIN) along with company name, contact details, industry, state, and revenue data. This allows B2B sales teams to target verified, active businesses for outreach." },
  { q: "How accurate is Datorra's GST data?", a: "Datorra uses multi-source verification — cross-referencing GST portal data, MCA records, and other government databases. Our GST sales purchase data is refreshed monthly, ensuring you get the most current and accurate information. We are a verified company data provider India businesses trust for data quality." },
  { q: "Can I get GST data for a specific state or industry?", a: "Yes. Datorra offers fully customizable filters. You can request GST data for a specific state (e.g., Maharashtra, Delhi, Gujarat), a specific industry (e.g., IT, Manufacturing, Wholesale), a turnover range, or any combination. We build custom datasets to match your exact requirements." },
  { q: "In what format is GST data delivered?", a: "GST data is delivered in Excel (.xlsx) and CSV formats — ready to import into any CRM, ERP, or analytics tool. For enterprise clients, API access is also available. Delivery is completed within 24 hours of order confirmation." },
  { q: "How is GST sales data different from GST registration data?", a: "GST registration data contains basic company information — name, GSTIN, address, registration date, and status. GST sales data goes deeper — it includes annual sales turnover, purchase amounts, input tax credit claimed, filing frequency, and transaction patterns. GST sales purchase data gives you a financial picture of the business, not just its registration details." },
];

const INTERNAL_LINKS = [
  { label: "GST Registered Companies", href: "/databases" },
  { label: "MCA Company Database", href: "/databases" },
  { label: "Real Estate Data", href: "/databases" },
  { label: "Stock Market Data", href: "/databases" },
  { label: "B2B Leads Database", href: "/databases" },
];

export default function GstData() {
  const [openFaq, setOpenFaq] = useState(0);

  useSeo({
    title: "GST Sales & Purchase Data Provider India | B2B Database | Datorra",
    description:
      "Buy verified GST sales purchase data in India. Company database with GST number, B2B leads, pan-India coverage. Download in Excel/CSV. Trusted by 1,200+ businesses. Call +91 85379 42196",
    path: "/gst-sales-purchase-data",
    jsonLd: [
      {
        id: "gst-breadcrumb",
        data: {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://datorra.in/" },
            { "@type": "ListItem", position: 2, name: "Databases", item: "https://datorra.in/databases" },
            { "@type": "ListItem", position: 3, name: "GST Sales & Purchase Data", item: "https://datorra.in/gst-sales-purchase-data" },
          ],
        },
      },
      {
        id: "gst-product",
        data: {
          "@context": "https://schema.org",
          "@type": "Product",
          name: "GST Sales & Purchase Data — B2B Database with GST Number",
          description:
            "Verified GST sales purchase data (GSTR-1 E-Invoice level) for B2B lead generation, credit analysis, market research and account-based marketing across Pan India.",
          brand: { "@type": "Brand", name: "Datorra India" },
          category: "Business Data / GST Database",
          areaServed: "IN",
          offers: {
            "@type": "Offer",
            priceCurrency: "INR",
            availability: "https://schema.org/InStock",
            url: "https://datorra.in/gst-sales-purchase-data",
            seller: { "@type": "Organization", name: "Datorra India", url: "https://datorra.in" },
          },
        },
      },
      {
        id: "gst-faq",
        data: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      },
    ],
  });

  return (
    <div data-testid="gst-data-page" className="bg-white">
      {/* HERO */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-20 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="text-xs md:text-sm text-slate-500 flex items-center gap-1.5">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300" strokeWidth={1.75} />
            <Link to="/databases" className="hover:text-blue-600">Databases</Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300" strokeWidth={1.75} />
            <span className="text-slate-900 font-medium">GST Sales &amp; Purchase Data</span>
          </nav>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-slate-900 leading-[1.03] max-w-5xl"
          >
            GST Sales &amp; Purchase Data <span className="font-serif-editorial italic">Provider</span> in India
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="mt-6 max-w-3xl text-lg md:text-xl text-slate-600 leading-relaxed"
          >
            Access verified B2B <strong className="font-medium text-slate-900">company database with GST number</strong>, sales turnover, purchase data —
            pan-India coverage, delivered in Excel/CSV within 24 hours by India's trusted <strong className="font-medium text-slate-900">gst data provider in india</strong>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <Link to="/contact" data-testid="gst-hero-quote" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 hover:shadow-xl hover:shadow-blue-500/20 active:scale-95 transition-all">
              Get Quote
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
            </Link>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" data-testid="gst-hero-whatsapp" aria-label="WhatsApp Datorra India" className="group inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white pl-6 pr-5 py-4 text-sm font-medium hover:brightness-95 hover:shadow-xl hover:shadow-green-500/20 active:scale-95 transition-all">
              <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
              WhatsApp Now
            </a>
            <Link to="/contact" data-testid="gst-hero-sample" className="group inline-flex items-center gap-2 rounded-full bg-white text-slate-900 border border-slate-200 pl-6 pr-5 py-4 text-sm font-medium hover:border-slate-900 hover:bg-slate-50 active:scale-95 transition-all">
              <FileSpreadsheet className="w-4 h-4 text-blue-600" strokeWidth={1.75} />
              Download Sample
            </Link>
          </motion.div>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-600">
            {["Verified Data", "Pan India Coverage", "Excel/CSV Delivery", "24hr Turnaround"].map((b) => (
              <span key={b} className="inline-flex items-center gap-2">
                <Check className="w-4 h-4 text-blue-600" strokeWidth={2.25} />
                <span className="font-medium text-slate-800">{b}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — WHAT IS */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Overview</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            What is <span className="font-serif-editorial italic">GST Sales &amp; Purchase Data</span>?
          </h2>
          <div className="mt-8 space-y-6 text-base md:text-lg text-slate-600 leading-relaxed">
            <p>
              GSTR-1 is the outward supply return filed by every GST-registered business — it contains details of all sales
              transactions, including buyer GSTIN, invoice values, taxable amounts, and tax collected. GSTR-3B is the monthly
              summary return showing total sales, total purchases, input tax credit claimed, and net tax liability. Together,{" "}
              <strong className="text-slate-900">gst sales purchase data</strong> gives you a complete picture of a company's
              business volume, trading patterns, and financial health.
            </p>
            <p>
              Banks and NBFCs use GST sales purchase data to verify business turnover before approving loans. CA firms and tax
              consultants use it to identify businesses that need GST compliance services. Market research firms analyze
              industry-wise turnover data for reports. B2B sales teams use a{" "}
              <strong className="text-slate-900">company database with gst number</strong> to target prospects by revenue range
              and industry. Credit rating agencies assess creditworthiness using GST filing patterns —
              this is why every serious growth team looks for genuine{" "}
              <strong className="text-slate-900">gst purchase data providers</strong>.
            </p>
            <p>
              Datorra is a trusted <strong className="text-slate-900">gst data provider in india</strong> with multi-source
              verification. Unlike other <strong className="text-slate-900">gst sales database providers</strong> that rely on a
              single source and update once a year, Datorra refreshes its GST data monthly, cross-verifies across government
              portals like the{" "}
              <a href="https://www.gst.gov.in" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline underline-offset-4 hover:text-blue-700">official GST portal</a>{" "}
              and the{" "}
              <a href="https://www.mca.gov.in" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline underline-offset-4 hover:text-blue-700">MCA portal</a>,
              and delivers in clean Excel/CSV within 24 hours. As a{" "}
              <strong className="text-slate-900">verified company data provider india</strong> businesses trust, Datorra covers
              all 28 states and 8 union territories.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4 — DATA FIELDS */}
      <section className="py-20 md:py-28 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Data Fields</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            What's included in our GST Sales &amp; Purchase database
          </h2>
          <p className="mt-6 max-w-2xl text-slate-600 text-base md:text-lg leading-relaxed">
            Every record ships with the real GSTR-1 E-Invoice fields — invoice-level accuracy, not summarized aggregates.
          </p>

          <h3 className="mt-12 font-display text-lg font-medium text-slate-900 uppercase tracking-[0.14em]">
            Sales Data — GSTR-1 B2B Fields
          </h3>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {DATA_FIELDS.map((f) => (
              <div key={f} className="flex items-start gap-3 py-2 border-b border-slate-200">
                <Check className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" strokeWidth={2.25} />
                <span className="text-slate-800 text-base">{f}</span>
              </div>
            ))}
          </div>

          <div className="mt-14 rounded-3xl bg-white border border-slate-100 p-8 md:p-10">
            <h3 className="font-display text-2xl font-medium text-slate-900">
              What GST sales data reveals
            </h3>
            <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
              {REVEALS.map((r) => (
                <li key={r} className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0" />
                  <span className="text-slate-700 leading-relaxed">{r}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="mt-8 text-sm text-slate-500">
            Available in Excel/CSV format — ready to import into any CRM, ERP, or analytics tool.
          </p>
        </div>
      </section>

      {/* SECTION 5 — USE CASES */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Use Cases</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Who uses GST Sales &amp; Purchase Data in India
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {USE_CASES.map((u) => {
              const Icon = u.icon;
              return (
                <div key={u.title} className="bg-[#fafafa] border border-slate-100 rounded-3xl p-7 hover:bg-white hover:shadow-md hover:border-blue-100 transition-all flex flex-col">
                  <div className="w-11 h-11 rounded-xl bg-white border border-slate-100 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-6 font-display text-lg font-medium text-slate-900">{u.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{u.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 6 — WHY DATORRA COMPARISON */}
      <section className="py-20 md:py-28 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Compare</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Why choose Datorra for GST Sales Purchase Data
          </h2>

          <div className="mt-12 overflow-hidden rounded-3xl border border-slate-200 bg-white">
            <div className="grid grid-cols-12 bg-white border-b border-slate-200 text-sm">
              <div className="col-span-6 md:col-span-4 p-5 font-medium text-slate-500 uppercase tracking-[0.14em] text-xs">Feature</div>
              <div className="col-span-3 md:col-span-4 p-5 font-display font-medium text-slate-900 bg-blue-50 border-l border-blue-100">
                <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-600" />Datorra</span>
              </div>
              <div className="col-span-3 md:col-span-4 p-5 font-medium text-slate-500">Other Providers</div>
            </div>
            {COMPARISON.map((row, i) => (
              <div key={row.f} className={`grid grid-cols-12 text-sm md:text-base ${i % 2 === 1 ? "bg-slate-50/50" : "bg-white"}`}>
                <div className="col-span-6 md:col-span-4 p-5 text-slate-700 font-medium border-t border-slate-100">{row.f}</div>
                <div className="col-span-3 md:col-span-4 p-5 text-slate-900 bg-blue-50/60 border-l border-t border-blue-100 flex items-start gap-2">
                  <Check className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" strokeWidth={2.25} />
                  <span className="font-medium">{row.d}</span>
                </div>
                <div className="col-span-3 md:col-span-4 p-5 text-slate-500 border-t border-slate-100">{row.o}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — SAMPLE PREVIEW */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Sample</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Sample GST Sales &amp; Purchase Database
          </h2>
          <p className="mt-6 max-w-2xl text-slate-600 text-base md:text-lg leading-relaxed">
            A snapshot of the real GSTR-1 E-Invoice format we deliver — request the full sample via our contact form.
          </p>

          <div className="mt-12 overflow-x-auto rounded-3xl border border-slate-200 bg-white">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                  <th className="p-4 font-medium">#</th>
                  <th className="p-4 font-medium">Buyer Company</th>
                  <th className="p-4 font-medium">Buyer GSTIN</th>
                  <th className="p-4 font-medium">Invoice Date</th>
                  <th className="p-4 font-medium">Invoice Value</th>
                  <th className="p-4 font-medium">State</th>
                  <th className="p-4 font-medium">Tax Rate</th>
                  <th className="p-4 font-medium">Taxable Value</th>
                </tr>
              </thead>
              <tbody>
                {SAMPLE_ROWS.map((r) => (
                  <tr key={r.i} className="border-b border-slate-100 last:border-0">
                    <td className="p-4 text-slate-500">{r.i}</td>
                    <td className={`p-4 text-slate-900 font-medium ${r.blur ? "filter blur-[5px] select-none" : ""}`}>{r.buyer}</td>
                    <td className={`p-4 text-slate-700 font-mono text-xs ${r.blur ? "filter blur-[5px] select-none" : ""}`}>{r.gstin}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[5px] select-none" : ""}`}>{r.date}</td>
                    <td className={`p-4 text-slate-900 font-medium ${r.blur ? "filter blur-[5px] select-none" : ""}`}>{r.value}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[5px] select-none" : ""}`}>{r.state}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[5px] select-none" : ""}`}>{r.rate}</td>
                    <td className={`p-4 text-slate-900 ${r.blur ? "filter blur-[5px] select-none" : ""}`}>{r.taxable}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 flex justify-center">
            <Link to="/contact" data-testid="gst-sample-cta" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 hover:shadow-lg hover:shadow-blue-500/20 transition-all">
              Request Full Sample — Contact Us
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 8 — PRICING */}
      <section className="py-20 md:py-28 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Pricing</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            GST Sales Purchase Data — <span className="font-serif-editorial italic">Pricing</span>
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {PRICING.map((p) => (
              <div key={p.t} className={`relative rounded-3xl p-8 flex flex-col ${p.featured ? "bg-slate-900 text-white border border-slate-900 shadow-2xl shadow-slate-900/20" : "bg-white border border-slate-100"}`}>
                {p.badge && (
                  <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 rounded-full bg-blue-600 text-white px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold">
                    {p.badge}
                  </span>
                )}
                <h3 className={`font-display text-2xl font-medium ${p.featured ? "text-white" : "text-slate-900"}`}>{p.t}</h3>
                <p className={`mt-4 text-sm leading-relaxed ${p.featured ? "text-slate-300" : "text-slate-600"}`}>{p.d}</p>
                <Link to={p.href} className={`mt-8 group inline-flex items-center gap-2 rounded-full pl-5 pr-4 py-3 text-sm font-medium self-start transition-all ${p.featured ? "bg-white text-slate-900 hover:bg-blue-600 hover:text-white" : "bg-slate-900 text-white hover:bg-blue-600"}`}>
                  {p.cta}
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
                </Link>
              </div>
            ))}
          </div>

          <p className="mt-10 text-slate-600 text-base md:text-lg text-center max-w-2xl mx-auto">
            All pricing is custom — we scope the exact dataset you need before quoting. No generic packages, no wasted data.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white pl-6 pr-5 py-4 text-sm font-medium hover:brightness-95 transition-all">
              <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
              WhatsApp Us
            </a>
            <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-white text-slate-900 border border-slate-200 pl-6 pr-5 py-4 text-sm font-medium hover:border-slate-900 hover:bg-slate-50 transition-all">
              Contact Form
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 9 — LONG FORM */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Guide</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            How to buy GST Data for marketing in India
          </h2>

          <div className="mt-12 space-y-14 text-base md:text-lg text-slate-700 leading-relaxed">
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">
                Step-by-step guide to buying a GST database in India
              </h3>
              <p className="mt-4">
                Buying a GST database doesn't have to be complicated. The first step is to identify your target audience —
                which states you want to cover, which industries you're selling into, and the turnover range that fits your ICP.
                Once you've defined that, choose a reliable{" "}
                <strong className="text-slate-900">gst data provider in india</strong> like Datorra. Request a paid sample slice
                to verify data quality — check the accuracy of GSTINs, freshness of turnover figures, and completeness of
                contact fields. Confirm the source verification methodology and the update cadence — anything older than
                three months is not acceptable for a serious campaign. Once you approve the scope, place the order and receive
                the final dataset in Excel or CSV within 24 hours. This is exactly how you{" "}
                <strong className="text-slate-900">buy gst database</strong> access from Datorra: share requirements on
                WhatsApp, review a custom quote and get delivery the next business day.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">
                What to look for in a GST data provider
              </h3>
              <p className="mt-4">
                Not all <strong className="text-slate-900">gst purchase data providers</strong> are built the same. Data
                freshness is the single most important factor — a database that's been sitting on someone's server for a year
                is useless for outbound. Look for monthly refresh cycles. The second filter is multi-source verification;
                genuine providers cross-check the GST portal against MCA records and other government sources to catch stale
                or invalid GSTINs. Coverage is the third filter: a true{" "}
                <strong className="text-slate-900">pan india b2b database provider</strong> covers all 28 states and 8 union
                territories, not just Maharashtra and Delhi. Delivery format matters too — Excel and CSV are the working
                minimum, and API access should be available for enterprise clients. Finally, customer support (WhatsApp is
                the honest test) and compliance posture (DPDP Act adherence, data-use agreements) tell you whether the vendor
                is a business partner or a spreadsheet seller. Datorra is the{" "}
                <strong className="text-slate-900">gst sales database providers</strong> shortlist most growth teams end up
                choosing because every one of those boxes is checked.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">
                Using GST Data for B2B marketing campaigns
              </h3>
              <p className="mt-4">
                Once you've bought the dataset, the value comes from how you use it. Start by building an ICP-matched prospect
                list — filter the GST database by industry code, state, and turnover band that maps to your ideal customer.
                Segment further by product category (what they sell) or purchase category (what they buy) using the GSTR-1
                E-Invoice fields. Layer in verified contact details and load the list into your CRM. From there, run
                account-based marketing plays using the{" "}
                <strong className="text-slate-900">company database with gst number</strong> — the GSTIN itself becomes a
                clean, unique identifier for de-duplication and enrichment. Many of our customers{" "}
                <strong className="text-slate-900">download b2b sales leads data excel</strong> from Datorra every month,
                slice it by campaign, and push directly into HubSpot, Zoho or Salesforce. If you're wondering{" "}
                <strong className="text-slate-900">how to buy gst data for marketing</strong> at scale, this workflow — buy,
                segment, sync, sequence — is the shortest path from list to pipeline.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">
                GST data for B2B lead generation
              </h3>
              <p className="mt-4">
                A high-quality{" "}
                <strong className="text-slate-900">b2b leads database india</strong> transforms the way sales teams prospect.
                Instead of guessing which accounts to prioritize, GST data lets you target by actual purchase category and
                turnover range — so a wholesaler can find only the buyers active in their product line, and a lender can focus
                on companies with the right revenue profile. Filing status becomes a proxy for business health: firms filing
                regularly with growing turnover are healthier prospects than those with intermittent returns. The combined
                filter — state, industry, turnover, filing regularity — narrows a Pan India universe of millions to a focused
                list of high-intent accounts your team can actually work.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">
                Downloading a GST registered companies list
              </h3>
              <p className="mt-4">
                If you're looking for a comprehensive{" "}
                <strong className="text-slate-900">gst registered companies list download</strong>, Datorra assembles it in
                Excel or CSV with GSTIN, company name, state, industry, turnover band, filing status and contact details.
                While the government GST portal exposes a basic registration search, it doesn't let you download bulk lists
                with turnover-level segmentation or invoice-level insight. Datorra's{" "}
                <strong className="text-slate-900">gst registered companies list download</strong> is more comprehensive
                because it merges live registration data with GSTR-1 filings and MCA records — giving you an audit-grade
                view of every registered entity in one dataset.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 10 — COVERAGE */}
      <section className="py-20 md:py-28 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Coverage</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Pan India GST database coverage
          </h2>

          <h3 className="mt-12 text-sm uppercase tracking-[0.16em] font-semibold text-slate-500">States (28)</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {STATES.map((s) => (
              <span key={s} className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-sm text-slate-800 hover:border-blue-600 hover:text-blue-700 transition-colors">
                <MapPin className="w-3.5 h-3.5 text-blue-600" strokeWidth={1.75} />
                {s}
              </span>
            ))}
          </div>

          <h3 className="mt-10 text-sm uppercase tracking-[0.16em] font-semibold text-slate-500">Union Territories (8)</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {UTS.map((u) => (
              <span key={u} className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-sm text-slate-800">
                <MapPin className="w-3.5 h-3.5 text-blue-600" strokeWidth={1.75} />
                {u}
              </span>
            ))}
          </div>

          <p className="mt-10 max-w-3xl text-slate-600 leading-relaxed">
            GST sales purchase data is available for every state and union territory — Maharashtra, Delhi, Gujarat, Karnataka,
            Tamil Nadu, Rajasthan, Uttar Pradesh, West Bengal, Telangana, Kerala, and all others. As a{" "}
            <strong className="text-slate-900">pan india b2b database provider</strong>, Datorra covers every corner of the
            country — and yes, that includes the Tier-2 and Tier-3 pincodes most vendors skip.
          </p>
        </div>
      </section>

      {/* SECTION 11 — FAQ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ FAQ</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            Frequently asked questions — GST Sales &amp; Purchase Data
          </h2>

          <div className="mt-12 divide-y divide-slate-200">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="py-1">
                  <button
                    data-testid={`gst-faq-toggle-${i}`}
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    aria-expanded={open}
                    className="w-full py-6 flex items-start justify-between gap-6 text-left group"
                  >
                    <span className="font-display text-lg md:text-xl font-medium text-slate-900 group-hover:text-blue-600 transition-colors">
                      {f.q}
                    </span>
                    <span className="mt-1 flex-shrink-0 w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center transition-colors group-hover:border-slate-900">
                      {open ? <Minus className="w-4 h-4 text-slate-900" strokeWidth={1.75} /> : <Plus className="w-4 h-4 text-slate-900" strokeWidth={1.75} />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.2, 0.7, 0.15, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="pb-6 pr-16 text-slate-600 leading-relaxed text-base md:text-lg">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 12 — CTA BANNER */}
      <section className="relative py-20 md:py-28 bg-slate-900 text-white overflow-hidden">
        <div className="absolute -top-16 -left-16 w-96 h-96 rounded-full bg-blue-600/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -right-16 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-300 font-semibold">/ Start</div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05]">
            Ready to access India's most verified{" "}
            <span className="font-serif-editorial italic">GST Sales &amp; Purchase</span> database?
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" data-testid="gst-banner-whatsapp" className="group inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white pl-7 pr-6 py-5 text-base font-medium hover:brightness-95 hover:shadow-xl hover:shadow-green-500/25 active:scale-95 transition-all">
              <MessageCircle className="w-5 h-5" strokeWidth={1.75} />
              WhatsApp Now
            </a>
            <Link to="/contact" data-testid="gst-banner-quote" className="group inline-flex items-center gap-2 rounded-full bg-white text-slate-900 pl-7 pr-6 py-5 text-base font-medium hover:bg-blue-50 hover:text-blue-700 active:scale-95 transition-all">
              Get Quote
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
            </Link>
          </div>
          <p className="mt-6 text-sm text-blue-200">1,200+ businesses trust Datorra for verified B2B data</p>
        </div>
      </section>

      {/* SECTION 13 — INTERNAL LINKS */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Related</div>
          <h2 className="mt-4 font-display text-2xl md:text-4xl font-light tracking-tight text-slate-900 leading-[1.05]">
            Explore more Datorra databases
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {INTERNAL_LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.href}
                className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-5 hover:border-slate-900 hover:shadow-md transition-all"
              >
                <span className="font-medium text-slate-900 text-sm">{l.label}</span>
                <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-slate-900 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" strokeWidth={1.75} />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
